class VariantImageSets {
  constructor(mediaGallery) {
    this.mediaGallery = mediaGallery;
    this.variantMediaPositions = this.parseVariantMediaPositions();
    this.mediaCount = parseInt(mediaGallery.dataset.mediaCount || 0);
    this.variantChangeUnsubscriber = undefined;
    this.mediaObserver = undefined;
    this.updateVisibilityTimeout = undefined;
    
    this.init();
  }
  
  init() {
    // Subscribe to variant change events
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
    }
    
    // Update media visibility on initial load
    this.updateMediaVisibility();
    
    // Observe for media updates (when product-info.js updates the gallery)
    const viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (viewer) {
      this.mediaObserver = new MutationObserver(() => {
        // Debounce to avoid multiple calls
        clearTimeout(this.updateVisibilityTimeout);
        this.updateVisibilityTimeout = setTimeout(() => {
          this.updateMediaVisibility();
        }, 100);
      });
      
      this.mediaObserver.observe(viewer, {
        childList: true,
        subtree: true
      });
    }
  }
  
  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.mediaObserver) {
      this.mediaObserver.disconnect();
    }
    if (this.updateVisibilityTimeout) {
      clearTimeout(this.updateVisibilityTimeout);
    }
  }
  
  parseVariantMediaPositions() {
    const positionsStr = this.mediaGallery.dataset.variantMediaPositions || '';
    if (!positionsStr) return {};
    
    const mapping = {};
    const pairs = positionsStr.split(',');
    for (const pair of pairs) {
      const [variantId, position] = pair.split(':');
      if (variantId && position) {
        mapping[variantId] = parseInt(position);
      }
    }
    return mapping;
  }
  
  handleVariantChange(event) {
    if (!event?.data?.variant) return;
    this.updateMediaVisibility(event.data.variant.id);
  }
  
  updateMediaVisibility(variantId = null) {
    // Get current variant ID from the selected variant script if not provided
    if (!variantId) {
      const variantSelects = this.mediaGallery.closest('product-info')?.querySelector('variant-selects') || 
                             document.querySelector('variant-selects');
      if (variantSelects) {
        const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
        if (selectedVariantScript) {
          try {
            const variant = JSON.parse(selectedVariantScript.textContent);
            variantId = variant?.id;
          } catch (error) {
            console.error('Error parsing selected variant:', error);
          }
        }
      }
    }
    
    if (!variantId) {
      // If no variant ID, show all media
      this.showAllMedia();
      return;
    }
    
    const variantIdStr = variantId.toString();
    const startPosition = this.variantMediaPositions[variantIdStr];
    
    if (startPosition === undefined) {
      // If variant not in mapping, show all media
      this.showAllMedia();
      return;
    }
    
    // Find the next variant's position (or end of media array)
    let endPosition = this.mediaCount;
    const sortedPositions = Object.values(this.variantMediaPositions).sort((a, b) => a - b);
    for (const pos of sortedPositions) {
      if (pos > startPosition) {
        endPosition = pos - 1;
        break;
      }
    }
    
    // Show/hide media items based on position
    this.showMediaByPositionRange(startPosition, endPosition);
  }
  
  showAllMedia() {
    const viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = this.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    const mediaItems = viewer?.querySelectorAll('li[data-media-position]') || [];
    const thumbnailItems = thumbnails?.querySelectorAll('li[data-media-position-index]') || [];
    
    mediaItems.forEach(item => {
      item.style.display = '';
      item.classList.remove('hidden');
    });
    
    thumbnailItems.forEach(thumb => {
      thumb.style.display = '';
      thumb.classList.remove('hidden');
    });
  }
  
  isGlobalImage(element) {
    // Check if the element or any of its descendants has an img with alt text containing "#global_"
    const img = element.querySelector('img');
    if (img && img.alt && img.alt.includes('#global_')) {
      return true;
    }
    return false;
  }
  
  showMediaByPositionRange(startPos, endPos) {
    const viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = this.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    const mediaItems = viewer?.querySelectorAll('li[data-media-position]') || [];
    const thumbnailItems = thumbnails?.querySelectorAll('li[data-media-position-index]') || [];
    
    mediaItems.forEach(item => {
      const position = parseInt(item.dataset.mediaPosition || 0);
      const isGlobal = this.isGlobalImage(item);
      
      if (isGlobal || (position >= startPos && position <= endPos)) {
        item.style.display = '';
        item.classList.remove('hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('hidden');
      }
    });
    
    thumbnailItems.forEach(thumb => {
      const position = parseInt(thumb.dataset.mediaPositionIndex || 0);
      const isGlobal = this.isGlobalImage(thumb);
      
      if (isGlobal || (position >= startPos && position <= endPos)) {
        thumb.style.display = '';
        thumb.classList.remove('hidden');
      } else {
        thumb.style.display = 'none';
        thumb.classList.add('hidden');
      }
    });
  }
}

// Initialize variant image sets on all media-gallery elements
function initializeVariantImageSets() {
  const mediaGalleries = document.querySelectorAll('media-gallery[data-variant-media-positions]');
  mediaGalleries.forEach((gallery) => {
    if (!gallery.variantImageSets) {
      gallery.variantImageSets = new VariantImageSets(gallery);
    }
  });
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVariantImageSets);
} else {
  initializeVariantImageSets();
}

// Also initialize on dynamically added media galleries
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) {
        // Check if the added node is a media-gallery or contains one
        if (node.tagName === 'MEDIA-GALLERY' && node.dataset.variantMediaPositions) {
          if (!node.variantImageSets) {
            node.variantImageSets = new VariantImageSets(node);
          }
        } else {
          const mediaGalleries = node.querySelectorAll?.('media-gallery[data-variant-media-positions]');
          if (mediaGalleries) {
            mediaGalleries.forEach((gallery) => {
              if (!gallery.variantImageSets) {
                gallery.variantImageSets = new VariantImageSets(gallery);
              }
            });
          }
        }
      }
    });
  });
});

if (document.body) {
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
} else {
  // If body doesn't exist yet, wait for it
  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
}

